// features/marketing/components/LeadMagnetTab.tsx
import { FC, useMemo, useState } from 'react';
import { getErrorMessage } from '@/utils/error';
import { format, formatDistanceToNow, isToday, isYesterday, parseISO } from 'date-fns';
import { useSxmAutomationConfig } from '@/hooks/useSxmAutomationConfig';
import { EditableDataTable } from '@/components/ui/EditableDataTable';
import type { EditableColumn } from '@/components/ui/EditableDataTable';
import { Modal, ModalFooter } from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import {
  FileText,
  BarChart2,
  Newspaper,
  PartyPopper,
  Briefcase,
  Mic,
  Smile,
  Linkedin,
  Facebook,
  Instagram,
  Twitter,
  Globe,
  ExternalLink,
  Upload,
  FileUp,
  FileDown,
  Link,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import { LeadImportModal } from './LeadImportModal';

export type LeadItem = {
  id: string;
  email: string;
  createdAt: string;
  platform?: string;
  mxmPost?: {
    id: number;
    title: string;
    url?: string; // LinkedIn URL
    facebookPostUrl?: string;
    instagramPostUrl?: string;
    twitterPostUrl?: string;
  };
  mxmUpload?: {
    id: string;
    title: string; // Title from mxm_upload table
    category: string;
    upload: {
      filename: string;
    };
  };
  mxmLeadCapturePage?: {
    id: string;
    title: string;
  };
  sourceUrl?: string;
  attemptCount?: number;
  status?: string;
  nextOutreachAt?: string;
  lastOutreachAt?: string;
};

interface LeadMagnetTabProps {
  data: LeadItem[];
  loading?: boolean;
  error?: string | null;
  pagination?: {
    pageIndex: number;
    pageSize: number;
    totalCount: number;
  };
  onPaginationChange?: (pagination: { pageIndex: number; pageSize: number }) => void;
  onGlobalSearch?: (query: string) => Promise<void>;
  globalFilterValue?: string;
  onRefresh?: () => void;
  userId?: string;
}

// Helper to get category color and icon
const getCategoryStyle = (category: string) => {
  const lower = category?.toLowerCase() || '';
  if (lower.includes('poll')) return { color: 'bg-indigo-100 text-indigo-700', icon: BarChart2 };
  if (lower.includes('launch') || lower.includes('update')) return { color: 'bg-emerald-100 text-emerald-700', icon: PartyPopper };
  if (lower.includes('case study')) return { color: 'bg-blue-100 text-blue-700', icon: FileText };
  if (lower.includes('news')) return { color: 'bg-amber-100 text-amber-700', icon: Newspaper };
  if (lower.includes('hiring') || lower.includes('role')) return { color: 'bg-purple-100 text-purple-700', icon: Briefcase };
  if (lower.includes('leadership')) return { color: 'bg-rose-100 text-rose-700', icon: Mic };
  if (lower.includes('humor')) return { color: 'bg-pink-100 text-pink-700', icon: Smile };
  if (lower.includes('imported csv')) return { color: 'bg-purple-100 text-purple-700', icon: FileDown };

  return { color: 'bg-slate-100 text-slate-700', icon: FileText };
};

// Helper for Avatar
const LeadAvatar = ({ email }: { email: string }) => {
  const initial = email ? email[0].toUpperCase() : '?';
  // simple hash for color
  const colors = [
    'bg-slate-100 text-slate-700', 'bg-red-100 text-red-700',
    'bg-orange-100 text-orange-700', 'bg-amber-100 text-amber-700',
    'bg-green-100 text-green-700', 'bg-emerald-100 text-emerald-700',
    'bg-teal-100 text-teal-700', 'bg-cyan-100 text-cyan-700',
    'bg-blue-100 text-blue-700', 'bg-indigo-100 text-indigo-700',
    'bg-violet-100 text-violet-700', 'bg-purple-100 text-purple-700',
    'bg-fuchsia-100 text-fuchsia-700', 'bg-rose-100 text-rose-700'
  ];
  const colorIndex = email.length % colors.length;
  const colorClass = colors[colorIndex];

  return (
    <div className={`h-8 w-8 rounded-full ${colorClass} flex items-center justify-center text-xs font-bold shadow-sm ring-1 ring-slate-100`}>
      {initial}
    </div>

  );
};

export const LeadMagnetTab: FC<LeadMagnetTabProps> = ({
  data,
  loading = false,
  error,
  pagination,
  onPaginationChange,
  onGlobalSearch,
  globalFilterValue,
  onRefresh,
  userId,
}) => {
  const { config } = useSxmAutomationConfig(userId);
  const maxSteps = config?.maxAttempts || 3;


  const handleGenerateBookingLink = async (leadId: string) => {
    try {
      const { default: api } = await import('@/utils/api');
      const res = await api.get(`/mxm/leads/${leadId}/booking-link`);
      if (res.data?.url) {
        navigator.clipboard.writeText(res.data.url);
        alert("Booking link copied to clipboard!");
      }
    } catch (err) {
      console.error("Failed to generate booking link", err);
      alert("Failed to generate booking link");
    }
  };

  const columns: EditableColumn<LeadItem>[] = useMemo(
    () => [
      {
        key: 'email',
        header: 'Lead Profile',
        editable: false,
        enableSorting: false,
        render: (row) => (
          <div className="flex items-center gap-4 py-2">
            <LeadAvatar email={row.email} />
            <div className="flex flex-col">
              <span className="text-sm font-bold text-slate-900">{row.email}</span>
              <span className="text-xs text-slate-500 font-medium tracking-wide">Captured Lead</span>
            </div>
          </div>
        )
      },
      {
        key: 'id',
        header: 'Lead Document',
        editable: false,
        enableSorting: false,
        render: (row) => {
          // 1. Post (Priority display)
          if (row.mxmPost) {
            return (
              <div className="flex items-center gap-3 w-full max-w-md group py-0.5">
                <div className="flex items-center justify-center w-8 h-8 rounded-md bg-indigo-50 text-indigo-600 shrink-0">
                  <FileText className="w-4 h-4" />
                </div>
                <div className="flex flex-col min-w-0 flex-grow gap-0.5">
                  <span className="text-sm font-bold text-slate-800 truncate" title={row.mxmPost.title}>
                    {row.mxmPost.title}
                  </span>
                  <span className="text-[10px] text-indigo-500 uppercase tracking-wide font-bold">
                    Social Media Post
                  </span>
                </div>
              </div>
            );
          }

          // 2. Collection
          if (row.mxmLeadCapturePage) {
            return (
              <div className="flex items-center gap-3 w-full max-w-md group py-0.5">
                <div className="flex items-center justify-center w-8 h-8 rounded-md bg-blue-50 text-blue-600 shrink-0">
                  <Globe className="w-4 h-4" />
                </div>
                <div className="flex flex-col min-w-0 flex-grow gap-0.5">
                  <span className="text-sm font-bold text-slate-800 truncate" title={row.mxmLeadCapturePage.title}>
                    {row.mxmLeadCapturePage.title}
                  </span>
                  <span className="text-[10px] text-blue-500 uppercase tracking-wide font-bold">
                    Document Collection
                  </span>
                </div>
              </div>
            );
          }

          // 3. Fallback to Single Document
          if (!row.mxmUpload?.title) {
            if (row.platform?.toLowerCase() === 'imported') {
              return (
                <div className="flex items-center gap-3 w-full max-w-md group py-0.5">
                  <div className="flex items-center justify-center w-8 h-8 rounded-md bg-purple-50 text-purple-600 shrink-0">
                    <FileDown className="w-4 h-4" />
                  </div>
                  <div className="flex flex-col min-w-0 flex-grow gap-0.5">
                    <span className="text-sm font-bold text-slate-800 truncate">
                      Imported Leads CSV
                    </span>
                    <span className="text-[10px] text-purple-500 uppercase tracking-wide font-bold">
                      Imported CSV
                    </span>
                  </div>
                </div>
              );
            }

            return (
              <span className="text-slate-400 italic">
                Direct Access
              </span>
            );
          }

          const { color, icon: Icon } = getCategoryStyle(row.mxmUpload.category);

          return (
            <div className="flex items-center gap-3 w-full max-w-md group py-0.5">
              <div className={`flex items-center justify-center w-8 h-8 rounded-md ${color.replace('bg-', 'bg-opacity-10 text-')} shrink-0`}>
                <Icon className="w-4 h-4" />
              </div>

              <div className="flex flex-col min-w-0 flex-grow gap-0.5">
                <span className="text-sm font-medium text-slate-700 truncate" title={row.mxmUpload.title}>
                  {row.mxmUpload.title}
                </span>
                <span className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold">
                  {row.mxmUpload.category}
                </span>
              </div>
            </div>
          );
        }
      },
      {
        key: 'platform',
        header: 'Source',
        editable: false,
        enableSorting: false,
        render: (row) => {
          // Get platform icon
          const getPlatformIcon = (platform?: string) => {
            switch (platform?.toLowerCase()) {
              case 'linkedin': return <Linkedin className="w-4 h-4 text-blue-600" />;
              case 'facebook': return <Facebook className="w-4 h-4 text-blue-500" />;
              case 'instagram': return <Instagram className="w-4 h-4 text-pink-500" />;
              case 'twitter': return <Twitter className="w-4 h-4 text-sky-500" />;
              case 'imported': return <FileUp className="w-4 h-4 text-purple-600" />;
              default: return <Globe className="w-4 h-4 text-slate-400" />;
            }
          };

          // Get platform-specific post URL
          const getPostUrl = (platform?: string, post?: LeadItem['mxmPost']) => {
            if (!post) return null;
            switch (platform?.toLowerCase()) {
              case 'linkedin': return post.url;
              case 'facebook': return post.facebookPostUrl;
              case 'instagram': return post.instagramPostUrl;
              case 'twitter': return post.twitterPostUrl;
              default: return null;
            }
          };

          if (!row.platform && !row.mxmPost) {
            return <span className="text-slate-400 italic text-xs">Direct</span>;
          }

          if (row.platform?.toLowerCase() === 'imported') {
            return (
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-purple-50">
                  <FileUp className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-bold text-slate-700">Imported</span>
                </div>
              </div>
            );
          }

          const postUrl = getPostUrl(row.platform, row.mxmPost);

          return (
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-slate-100">
                {getPlatformIcon(row.platform)}
              </div>
              <div className="flex flex-col min-w-0">
                {postUrl ? (
                  <a
                    href={postUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline truncate max-w-[120px] flex items-center gap-1"
                    title={row.mxmPost?.title}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {row.mxmPost?.title || 'View Post'}
                    <ExternalLink className="w-3 h-3 shrink-0" />
                  </a>
                ) : (
                  <span className="text-xs font-medium text-slate-700 truncate max-w-[120px]" title={row.mxmPost?.title || 'External Source'}>
                    {row.mxmPost?.title || (row.mxmLeadCapturePage ? 'Page Visit' : 'Link Click')}
                  </span>
                )}
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-[10px] text-slate-400 capitalize truncate">
                    {row.platform || (row.mxmLeadCapturePage ? 'Public Page' : 'Direct Link')}
                  </span>
                  {row.sourceUrl && (
                    <a
                      href={row.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 p-0.5 hover:bg-slate-100 rounded transition-colors"
                      title={`Full Source: ${row.sourceUrl}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="w-2 h-2 text-slate-300" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        }
      },
      {
        key: 'createdAt',
        header: 'Captured',
        editable: false,
        enableSorting: false,
        render: (row) => {
          const date = parseISO(row.createdAt);
          let dateStr = format(date, 'MMM d, yyyy');
          if (isToday(date)) dateStr = 'Today';
          else if (isYesterday(date)) dateStr = 'Yesterday';
          else if (new Date().getTime() - date.getTime() < 7 * 24 * 60 * 60 * 1000) dateStr = format(date, 'MMM d');

          return (
            <div className="flex flex-col py-1">
              <span className="text-sm font-bold text-slate-700">
                {dateStr}
              </span>
              <span className="text-[10px] text-slate-400 font-medium tabular-nums">
                {format(date, 'hh:mm a')}
              </span>
            </div>
          );
        }
      },
      {
        key: 'attemptCount',
        header: 'Automation',
        editable: false,
        enableSorting: false,
        render: (row) => {
          const count = row.attemptCount || 0;
          const status = row.status?.toLowerCase() || 'new';
          const nextAt = row.nextOutreachAt ? new Date(row.nextOutreachAt) : null;
          const lastAt = row.lastOutreachAt ? new Date(row.lastOutreachAt) : null;

          if (status === 'booked') {
            return (
              <div className="flex items-center gap-1.5 text-emerald-600 font-bold py-1">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-[11px] uppercase tracking-tight">Meeting Booked</span>
              </div>
            );
          }

          if (status === 'canceled' || status === 'inactive') {
            return (
              <div className="flex items-center gap-1.5 text-slate-400 py-1">
                <span className="text-[11px] font-semibold uppercase tracking-tight">Automation Paused</span>
              </div>
            );
          }

          return (
            <div className="flex flex-col gap-1 py-1">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  {Array.from({ length: maxSteps }, (_, i) => i + 1).map((s) => (
                    <div
                      key={s}
                      className={`h-1.5 w-1.5 rounded-full ${s <= count ? 'bg-blue-600' : 'bg-slate-200'}`}
                    />
                  ))}
                </div>
                <span className="text-[11px] font-bold text-slate-700 uppercase tracking-tight">
                  {count === 0 ? 'Waitlist' : `Step ${count}/${maxSteps}`}
                </span>
              </div>
              <div className="flex flex-col text-[10px] text-slate-400 font-medium whitespace-nowrap">
                {lastAt && (
                  <span className="flex items-center gap-1">
                    Last: {formatDistanceToNow(lastAt, { addSuffix: true })}
                  </span>
                )}
                {nextAt && (
                  <span className="flex items-center gap-1 text-slate-500 font-bold">
                    <Clock className="w-2.5 h-2.5" />
                    Next: {formatDistanceToNow(nextAt, { addSuffix: true })}
                  </span>
                )}
              </div>
            </div>
          );
        }
      },
      {
        key: 'actions',
        header: 'Booking',
        editable: false,
        enableSorting: false,
        render: (row) => (
          <div className="flex items-center justify-center h-full">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleGenerateBookingLink(row.id);
              }}
              className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200"
              title="Copy Booking Link"
            >
              <Link className="w-4 h-4" />
            </button>
          </div>
        )
      },
    ],
    [],
  );

  /* Export Logic */
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportDateRange, setExportDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [isExporting, setIsExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const { default: api } = await import('@/utils/api');
      const res = await api.get('/mxm/leads/export', {
        params: {
          startDate: exportDateRange.start,
          endDate: exportDateRange.end
        }
      });

      if (res.data?.url) {
        window.open(res.data.url, '_blank');
        setShowExportModal(false);
      } else {
        alert("Failed to generate export URL");
      }
    } catch (err: unknown) {
      console.error("Export failed", err);
      alert(getErrorMessage(err));
    } finally {
      setIsExporting(false);
    }
  };

  const renderHeaderActions = () => (
    <>
      <Button
        onClick={() => setShowExportModal(true)}
        variant="secondary"
        size="sm"
        className="ml-2 gap-2 text-slate-600 border-slate-300 hover:bg-slate-50"
      >
        <FileText className="w-4 h-4" />
        Export CSV
      </Button>
      <Button
        onClick={() => setShowImportModal(true)}
        variant="secondary"
        size="sm"
        className="ml-2 gap-2 text-slate-600 border-slate-300 hover:bg-slate-50"
      >
        <Upload className="w-4 h-4" />
        Import CSV
      </Button>
    </>
  );

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <EditableDataTable<LeadItem>
          title="Captured Leads"
          columns={columns}
          data={data}
          keyExtractor={(row) => row.id}
          enableEditing={false}
          enableDelete={false}
          loading={loading}
          pagination={pagination && {
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
            totalCount: pagination.totalCount,
          }}
          onPaginationChange={onPaginationChange}
          onGlobalSearch={onGlobalSearch}
          globalFilterValue={globalFilterValue}
          renderHeaderActions={renderHeaderActions}
        />
      </div>

      {/* Export Modal */}
      <Modal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="Export Leads"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-500">
            Select a date range to export leads (max 31 days).
          </p>

          {/* Quick Select Chips */}
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Today', action: 'today' },
              { label: 'Yesterday', action: 'yesterday' },
              { label: 'Last 7 Days', action: 'last7' },
              { label: 'Last 30 Days', action: 'last30' },
            ].map(preset => (
              <button
                key={preset.label}
                onClick={() => {
                  const end = new Date();
                  const start = new Date();

                  if (preset.action === 'yesterday') {
                    start.setDate(end.getDate() - 1);
                    end.setDate(end.getDate() - 1);
                  } else if (preset.action === 'last7') {
                    start.setDate(end.getDate() - 7);
                  } else if (preset.action === 'last30') {
                    start.setDate(end.getDate() - 30);
                  }
                  // 'today' is default (start=now, end=now)

                  setExportDateRange({
                    start: start.toISOString().split('T')[0],
                    end: end.toISOString().split('T')[0]
                  });
                }}
                className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors border border-transparent hover:border-slate-300"
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-700">Start Date</label>
              <input
                type="date"
                value={exportDateRange.start}
                max={exportDateRange.end}
                onChange={(e) => setExportDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-700">End Date</label>
              <input
                type="date"
                value={exportDateRange.end}
                min={exportDateRange.start}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => setExportDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>
          </div>
        </div>

        <ModalFooter>
          <Button
            variant="ghost"
            onClick={() => setShowExportModal(false)}
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={isExporting}
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
          >
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Lead Import Modal */}
      <LeadImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => {
          onRefresh?.();
        }}
        userId={userId}
      />
    </div>
  );
};
